import React, { useState, memo } from 'react';
import { useTranslation } from '../../../../contexts/I18nProvider';
import { Package, Lock, Unlock, Eye, User } from 'lucide-react';
import { PListDto } from '../../../../types/picking';

interface PickingCardActionsProps {
  list: PListDto;
  isOwned: boolean;
  onStartPicking: (list: PListDto) => Promise<void>;
  onLockList: (pListId: number) => Promise<void>;
  onUnlockList: (pListId: number) => Promise<void>;
  onPreviewList: (pListId: number) => void;
}

// Separate component for actions to isolate loading states
export const PickingCardActions: React.FC<PickingCardActionsProps> = memo(({
  list,
  isOwned,
  onStartPicking,
  onLockList,
  onUnlockList,
  onPreviewList
}) => {
  const { t } = useTranslation();
  const [startingList, setStartingList] = useState(false);
  const [lockingList, setLockingList] = useState(false);

  const handleStartPicking = async () => {
    try {
      setStartingList(true);
      await onStartPicking(list);
    } catch (err) {
      console.error('Error starting picking:', err);
    } finally {
      setStartingList(false);
    }
  };

  const handleLockList = async () => {
    try {
      setLockingList(true);
      await onLockList(list.pListID);
    } catch (err) {
      console.error('Error locking list:', err);
    } finally {
      setLockingList(false);
    }
  };

  const handleUnlockList = async () => {
    try {
      setLockingList(true);
      await onUnlockList(list.pListID);
    } catch (err) {
      console.error('Error unlocking list:', err);
    } finally {
      setLockingList(false);
    }
  };

  return (
    <div className="flex gap-2">
      <button
        onClick={handleStartPicking}
        disabled={startingList}
        className="flex items-center gap-1 bg-blue-600 text-white px-2 py-1 rounded text-xs hover:bg-blue-700 transition-colors disabled:opacity-50 min-w-[60px] justify-center"
      >
        {startingList ? (
          <>
            <div className="w-2.5 h-2.5 animate-spin border border-white border-t-transparent rounded-full" />
          </>
        ) : (
          <>
            <Package size={12} />
            <span>{t('warehouse.modules.picking.actions.start')}</span>
          </>
        )}
      </button>
      
      {isOwned ? (
        <button
          onClick={handleUnlockList}
          disabled={lockingList}
          className="flex items-center gap-1 bg-red-100 text-red-700 px-2 py-1 rounded text-xs hover:bg-red-200 transition-colors disabled:opacity-50 min-w-[60px] justify-center"
        >
          {lockingList ? (
            <>
              <div className="w-2.5 h-2.5 animate-spin border border-red-500 border-t-transparent rounded-full" />
            </>
          ) : (
            <>
              <Unlock size={12} />
              <span>{t('warehouse.modules.picking.actions.unlockList')}</span>
            </>
          )}
        </button>
      ) : (
        <button
          onClick={handleLockList}
          disabled={lockingList}
          className="flex items-center gap-1 bg-green-100 text-green-700 px-2 py-1 rounded text-xs hover:bg-green-200 transition-colors disabled:opacity-50 min-w-[70px] justify-center"
        >
          {lockingList ? (
            <>
              <div className="w-2.5 h-2.5 animate-spin border border-green-500 border-t-transparent rounded-full" />
            </>
          ) : (
            <>
              <Lock size={12} />
              <span>{t('warehouse.modules.picking.actions.lockList')}</span>
            </>
          )}
        </button>
      )}

      <button
        onClick={() => onPreviewList(list.pListID)}
        className="flex items-center gap-1 bg-gray-100 text-gray-700 px-2 py-1 rounded text-xs hover:bg-gray-200 transition-colors min-w-[50px] justify-center"
      >
        <Eye size={12} />
        <span>{t('warehouse.modules.picking.actions.preview')}</span>
      </button>
    </div>
  );
});

PickingCardActions.displayName = 'PickingCardActions';

interface PickingCardProps {
  list: PListDto;
  isOwned: boolean;
  onStartPicking: (list: PListDto) => Promise<void>;
  onLockList: (pListId: number) => Promise<void>;
  onUnlockList: (pListId: number) => Promise<void>;
  onPreviewList: (pListId: number) => void;
}

// Main card component with memoization
export const PickingCard: React.FC<PickingCardProps> = memo(({
  list,
  isOwned,
  onStartPicking,
  onLockList,
  onUnlockList,
  onPreviewList
}) => {
  const { t } = useTranslation();
  const getPriorityColor = (priority: number) => {
    if (priority >= 900) return 'bg-red-500';
    if (priority >= 500) return 'bg-orange-500';
    return 'bg-blue-500';
  };

  const getStatusColor = (status: number) => {
    switch (status) {
      case 10: return 'bg-green-500'; // Godkjent
      case 20: return 'bg-blue-500';  // Under arbeid
      case 30: return 'bg-yellow-500'; // Behandlet, ikke komplett
      case 40: return 'bg-green-600';  // Behandlet, komplett
      case 45: return 'bg-purple-500'; // Til pakking
      default: return 'bg-gray-400';   // Default/Ubehandlet
    }
  };

  const getStatusText = (status: number) => {
    switch (status) {
      case 0: return t('warehouse.modules.picking.status.unprocessed');
      case 10: return t('warehouse.modules.picking.status.approved');
      case 20: return t('warehouse.modules.picking.status.inProgress');
      case 30: return t('warehouse.modules.picking.status.processedIncomplete');
      case 40: return t('warehouse.modules.picking.status.processedComplete');
      case 45: return t('warehouse.modules.picking.status.toPacking');
      case 50: return t('warehouse.modules.picking.status.readyForExport');
      case 60: return t('warehouse.modules.picking.status.exported');
      default: return t('warehouse.modules.picking.status.unknown', { '0': status.toString() });
    }
  };

  return (
    <div
      className={`bg-white rounded-lg shadow-sm border-2 p-3 transition-all duration-200 ${
        isOwned 
          ? 'border-green-200 bg-green-50 hover:shadow-md hover:border-green-300' 
          : 'border-gray-200 hover:shadow-md hover:border-gray-300'
      }`}
    >
      {/* Responsive Layout with flex wrap */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        {/* Left side - Status dot + ID + Actions (desktop) */}
        <div className="flex items-center gap-3">
          <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${getStatusColor(list.pListStatus)}`} />
          <h3 className="text-sm font-semibold text-gray-900 whitespace-nowrap">
            #{list.pListID}
          </h3>
          {/* Actions - hidden on mobile, shown on desktop */}
          <div className="hidden sm:flex">
            <PickingCardActions
              list={list}
              isOwned={isOwned}
              onStartPicking={onStartPicking}
              onLockList={onLockList}
              onUnlockList={onUnlockList}
              onPreviewList={onPreviewList}
            />
          </div>
        </div>

        {/* Center - Description */}
        <div className="flex-1 px-3 min-w-0">
          <div className="text-xs text-gray-600 truncate">
            <span className="font-bold">{list.description1 || '-'}</span>
            {list.description2 && (
              <span className="text-gray-500 ml-1">| {list.description2}</span>
            )}
          </div>
        </div>

        {/* Right - Lines count + Priority (desktop) */}
        <div className="hidden sm:flex items-center gap-1 text-xs text-gray-600 whitespace-nowrap flex-shrink-0">
          <Package size={12} className="text-gray-400" />
          <span className="font-medium">{list.numOfOrderLines || 0}</span>
          <span className="text-gray-500">P:{list.priority}</span>
        </div>

        {/* Mobile second line - Actions + Lines count + Priority */}
        <div className="flex sm:hidden items-center justify-between w-full">
          <PickingCardActions
            list={list}
            isOwned={isOwned}
            onStartPicking={onStartPicking}
            onLockList={onLockList}
            onUnlockList={onUnlockList}
            onPreviewList={onPreviewList}
          />
          <div className="flex items-center gap-1 text-xs text-gray-600 whitespace-nowrap">
            <Package size={12} className="text-gray-400" />
            <span className="font-medium">{list.numOfOrderLines || 0}</span>
            <span className="text-gray-500">P:{list.priority}</span>
          </div>
        </div>
      </div>
    </div>
  );
}, (prevProps, nextProps) => {
  // Custom comparison function for memo
  // Only re-render if essential props change, not loading states
  return (
    prevProps.list.pListID === nextProps.list.pListID &&
    prevProps.list.pListStatus === nextProps.list.pListStatus &&
    prevProps.list.lockedBy === nextProps.list.lockedBy &&
    prevProps.isOwned === nextProps.isOwned &&
    prevProps.list.priority === nextProps.list.priority &&
    prevProps.list.description1 === nextProps.list.description1 &&
    prevProps.list.description2 === nextProps.list.description2 &&
    prevProps.list.numOfOrderLines === nextProps.list.numOfOrderLines
  );
});

PickingCard.displayName = 'PickingCard';